import React, { useState, useEffect } from 'react'
import { useMutation, useLazyQuery } from '@apollo/react-hooks';

import plusCircled from '../../../../../../images/plus-circled.svg';
import rewardCardCostGem from '../../../../../../images/reward-card-cost-gem.svg';
import rewardCardDelete from '../../../../../../images/reward-card-delete.svg';

import styles from './PackCrads.module.scss';

import { REWARD_CARDS_BY_ID, CREATE_REWARD_CARD, DELETE_REWARD_CARD, EDIT_REWARD_CARD } from './services/graphql';
import { GET_S3_UPLOAD_QUERY } from '../../../../services/graphql';
import { useApolloClient } from '@apollo/react-hooks';
import axios from 'axios';

const FIELDS_TO_TRACK = ['name', 'power', 'speed', 'skill', 'price', 'url'];

const snapshotOf = (card) => JSON.stringify(
    FIELDS_TO_TRACK.reduce((acc, field) => ({ ...acc, [field]: card[field] }), {})
);

const isCardSaved = (card) => !!card.id;
const isCardDirty = (card) => !card.savedSnapshot || snapshotOf(card) !== card.savedSnapshot;


const PackCards = ({ rewardPack, isNewPack, packDesignUrl, savedCardPackId }) => {
    const MAX_STAT_VALUE = 999;
    const MIN_STAT_VALUE = 0;
    const [packCards, setPackCards] = useState([]);
    const client = useApolloClient();

    const [loadRewardCards, { loading, error }] = useLazyQuery(REWARD_CARDS_BY_ID, {
        fetchPolicy: 'network-only',
        variables: {
            rewardCardPackId: rewardPack.id
        },
        onCompleted: (data) => {
            if (data) {
                const cards = data.rewardCardsById.map((card) => ({
                    ...card,
                    savedSnapshot: snapshotOf(card),
                }));
                setPackCards(cards);
            }
        }
    });

    const addCard = () => {
        setPackCards((prev) => [
            ...prev,
            { name: '', power: 0, speed: 0, skill: 0, url: '', cost: 0 },
        ]);
    };

    const [createRewardCard] = useMutation(CREATE_REWARD_CARD, {
        onCompleted: (data) => {
            if (data.createRewardCard.rewardCard) {
                loadRewardCards();
            }
        },
        onError: (error) => {
            alert('Card could not be created');
        }
    });

    const [updateRewardCard] = useMutation(EDIT_REWARD_CARD, {
        onCompleted: (data) => {
            if (data.editRewardCard.rewardCard) {
                loadRewardCards();
            }
        },
        onError: (error) => {
            alert('Card could not be updated');
        }
    });

    const saveCard = (index) => {
        const cardToBeSaved = packCards[index];
        if (!cardToBeSaved) return;

        const variables = {
            name: cardToBeSaved.name,
            price: cardToBeSaved.price,
            power: cardToBeSaved.power,
            speed: cardToBeSaved.speed,
            skill: cardToBeSaved.skill,
            rewardCardPackId: rewardPack.id || savedCardPackId,
            chapterWinnerOnly: false,
            url: cardToBeSaved.url
        };

        if (cardToBeSaved.id) {
            updateRewardCard({
                variables: {
                    ...variables,
                    rewardCardId: cardToBeSaved.id,
                }
            });
        } else {
            createRewardCard({ variables });
        }
    };

    const [deleteCard] = useMutation(DELETE_REWARD_CARD, {
        onCompleted: (data) => {
            if (data.deleteRewardCard.deleted) {
                loadRewardCards();
            }
        },
        onError: (error) => {
            console.error('Delete failed', error);
            alert('Could not delete card');
        },
    });

    const removeCard = (indexToRemove, card) => {
        if (isNewPack || !card.id) {
            setPackCards((prev) => prev.filter((_, index) => index !== indexToRemove));
        } else {
            deleteCard({
                variables: {
                    rewardCardId: card.id
                },
            });
        }
    };

    const updateCard = (index, field, value) => {
        setPackCards((prev) =>
            prev.map((card, i) => (i === index ? { ...card, [field]: value } : card))
        );
    };

    const updateStat = (index, field, rawValue) => {
        const value = Number(rawValue);
        if (value > MAX_STAT_VALUE) {
            return;
        }
        if (rawValue === '') {
            updateCard(index, field, 0);
            return;
        }

        updateCard(index, field, value);
    };

    useEffect(() => {
        if (isNewPack) return;

        loadRewardCards();
    }, [isNewPack]);

    const onSelectFile = (files, objectName, index) => {
        if (files.length) {
            const extension = files[0].name.split('.').pop().toLowerCase();
            const filename = `${objectName}-${new Date().getTime()}.${extension}`;
            client
                .query({
                    query: GET_S3_UPLOAD_QUERY,
                    variables: {
                        name: filename,
                        fileType: extension,
                        object: objectName,
                    },
                })
                .then((r) => {
                    const options = {
                        headers: {
                            'Content-Type': '',
                            'x-amz-acl': 'public-read',
                        },
                    };
                    axios
                        .put(r.data.s3UploadUrlFor.uploadUrl, files[0], options)
                        .then((s3r) => {
                            updateCard(index, 'url', r.data.s3UploadUrlFor.uploadUrl.split('?')[0]);
                        });
                });
        }
    };

    return (
        <div className={styles.packCardsContainer}>
            <div
                onClick={() => addCard()} className={styles.addCardPlaceholder}>
                <div
                    target='blank'
                    className={`${styles.rewardPackWrapper} ${styles.emptyRewardPack}`}
                    rel='noopener noreferrer'
                >
                    <img width='48' src={plusCircled} alt='plus-circle-svg' />
                </div>
            </div>
            {
                packCards.map((card, index) => {
                    const saved = isCardSaved(card);
                    const dirty = isCardDirty(card);

                    return <div key={index}>
                        <div
                            className={styles.card}
                            style={{
                                backgroundImage: `url(${isNewPack && packDesignUrl ? packDesignUrl : rewardPack.designImageUrl})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                            }}
                        >
                            <div className={styles.cardName}>
                                <input
                                    value={card.name}
                                    onChange={(e) => updateCard(index, 'name', e.target.value)}
                                />
                            </div>
                            <div className={styles.cardInnerCircle}>
                                {
                                    card.url ?
                                        <img src={card.url} /> :
                                        <div>
                                            <img width={35} src={plusCircled} alt='plus-circle-svg' />

                                            <input
                                                name='resources'
                                                className={`${styles.fileUpload} basic-form__text-box`}
                                                placeholder='Artwork'
                                                type='file'
                                                accept='image/*'
                                                onChange={(e) => {
                                                    onSelectFile(e.target.files, 'rewardCardResources', index)
                                                }}
                                            />
                                        </div>
                                }

                            </div>
                            <div className={styles.abilities}>
                                <div className={styles.power}>
                                    <input
                                        type='number'
                                        max={MAX_STAT_VALUE}
                                        min={MIN_STAT_VALUE}
                                        value={card.power === 0 ? '' : card.power}
                                        onChange={(e) => updateStat(index, 'power', e.target.value)}
                                    />
                                </div>
                                <div className={styles.speed}>
                                    <input
                                        type='number'
                                        max={MAX_STAT_VALUE}
                                        min={MIN_STAT_VALUE}
                                        value={card.speed === 0 ? '' : card.speed}
                                        onChange={(e) => updateStat(index, 'speed', e.target.value)}
                                    />
                                </div>
                                <div className={styles.skill}>
                                    <input
                                        type='number'
                                        max={MAX_STAT_VALUE}
                                        min={MIN_STAT_VALUE}
                                        value={card.skill === 0 ? '' : card.skill}
                                        onChange={(e) => updateStat(index, 'skill', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={styles.costDeleteContainer}>
                            <div className={styles.cardCost}>
                                <img width={24} src={rewardCardCostGem} alt='Reward Card Cost Gem' />
                                <input
                                    type='number'
                                    max={MAX_STAT_VALUE}
                                    min={MIN_STAT_VALUE}
                                    value={card.price === 0 ? '' : card.price}
                                    onChange={(e) => updateStat(index, 'price', e.target.value)}
                                />
                            </div>
                            <div
                                className={styles.cardDelete}
                                onClick={() => removeCard(index, card)}
                            >
                                <img width={19} src={rewardCardDelete} alt='Reward Card Delete' />
                            </div>
                            <div
                                className={saved && !dirty ? styles.disabled : ''}
                                onClick={() => {
                                    if (saved && !dirty) return;
                                    saveCard(index);
                                }}
                            >
                                <img width={35} src={plusCircled} alt='plus-circle-svg' />
                            </div>
                        </div>
                    </div>
                }

                )
            }
        </div>
    )
}

export default PackCards;